import React from 'react';
import StatusTableFilter from '../../../../../../../../../components/ui/StatusTableFilter/StatusTableFilter';
import { colorMaps, filterKeys, filterPaths } from '../../../config/filter';
import { getValueByPath } from '../../../../../../../../../../../helpers/getValueByPath';
import { stageIdsForAcceptanceStatuses } from '../../../config/room';
import {
  stageAddressLifeCycleNames,
  stageToStepMap,
} from '../../../../../../../../../constants/addressLifeCycle';

export const getStepStageLabel = ({ stepId, stepName }) => {
  if (!stepId) return stepName;

  const stageEntry = Object.entries(stageToStepMap).find(([, value]) =>
    value.steps.includes(stepId)
  );

  if (!stageEntry) return stepName;

  const [stageId] = stageEntry;

  const stageName = stageAddressLifeCycleNames[stageId];

  return stageName ? `${stageName} - ${stepName}` : stepName;
};

export const createFiltersForDisplay = ({ filters }) =>
  Object.entries(filters).reduce((acc, { 0: key, 1: valueMap }) => {
    const hasValue =
      key === filterKeys.acceptanceStatuses
        ? valueMap.size > 0
        : valueMap.size > 1;

    if (hasValue) {
      acc[key] = Array.from(valueMap.values())
        .sort((a, b) => {
          if (key === filterKeys.steps) {
            return a.id - b.id;
          }
          return b.count - a.count;
        })
        .map(({ id, name, count }) => {
          const stageName =
            key === filterKeys.steps
              ? getStepStageLabel({ stepId: id, stepName: name })
              : name;

          return {
            value: name,
            text: (
              <StatusTableFilter
                stageCount={count}
                stageName={stageName}
                indicatorBgColor={colorMaps[key]?.get(id)}
              />
            ),
          };
        });
    }
    return acc;
  }, {});

const addItem = ({ id, value, map, condition = true }) => {
  if (id && condition && !map.has(id)) {
    map.set(id, value);
  }
};

export const getAvailableFilters = ({ data }) => {
  const availableFilters = {
    [filterKeys.types]: new Map(),
    [filterKeys.stages]: new Map(),
    [filterKeys.acceptanceStatuses]: new Map(),
    [filterKeys.steps]: new Map(),
    [filterKeys.sales]: new Map(),
  };

  data.forEach((item) => {
    const { address, addressLifeCycle } = item;

    const { typeId, typeName } = address || {};
    const { id: stageId, name: stageName } =
      addressLifeCycle?.stageAddressLifeCycle || {};
    const { id: acceptanceStatusId, name: acceptanceStatusName } =
      addressLifeCycle?.acceptanceStatusAddressLifeCycle || {};
    const { id: stepId, name: stepName } =
      addressLifeCycle?.stepAddressLifeCycle || {};
    const { id: saleId, name: saleName } =
      addressLifeCycle?.saleAddressLifeCycle || {};

    const values = [
      {
        id: typeId,
        value: { id: typeId, name: typeName, count: 0 },
        map: availableFilters[filterKeys.types],
      },
      {
        id: stageId,
        value: { id: stageId, name: stageName, count: 0 },
        map: availableFilters[filterKeys.stages],
      },
      {
        id: acceptanceStatusId,
        value: {
          id: acceptanceStatusId,
          name: acceptanceStatusName,
          count: 0,
        },
        condition: stageIdsForAcceptanceStatuses.has(stageId),
        map: availableFilters[filterKeys.acceptanceStatuses],
      },
      {
        id: stepId,
        value: { id: stepId, name: stepName, count: 0 },
        map: availableFilters[filterKeys.steps],
      },
      {
        id: saleId,
        value: { id: saleId, name: saleName, count: 0 },
        map: availableFilters[filterKeys.sales],
      },
    ];

    values.forEach(addItem);
  });

  return availableFilters;
};

export const createFiltersData = ({ data, availableFilters }) => {
  if (!data.length)
    return createFiltersForDisplay({ filters: availableFilters });

  const filters = {};

  data.forEach((item) => {
    Object.entries(availableFilters).forEach(({ 0: key, 1: filtersData }) => {
      const hasFilters =
        key === filterKeys.acceptanceStatuses
          ? filtersData.size > 0
          : filtersData.size > 1;

      if (hasFilters) {
        if (!filters[key]) {
          filters[key] = new Map();
        }

        filtersData.forEach((value, id) => {
          if (!filters[key].has(id)) {
            // value клонируется,
            // потому что это value относится к avaialbleFilters и не должно изменяться
            filters[key].set(id, { ...value });
          }

          const canIncrementCount =
            key === filterKeys.acceptanceStatuses
              ? stageIdsForAcceptanceStatuses.has(
                  getValueByPath(item, filterPaths[filterKeys.stages])
                )
              : true;

          const isMatch = id === getValueByPath(item, filterPaths[key]);

          if (canIncrementCount && isMatch) {
            const currentVal = filters[key].get(id);
            currentVal.count += 1;
          }
        });
      }
    });
  });

  return createFiltersForDisplay({ filters });
};
